import { Component, ViewContainerRef, OnInit, OnDestroy, ViewChild, AfterViewInit, ElementRef, Input, AfterViewChecked } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { Cue5050Service } from '../../../_services/fundraising/cue5050.service';
import { Cue5050 } from '../../../_models/fundraising/Cue5050';
import { TicketOrder } from '../../../_models/fundraising/TicketOrder';
import { formatDateTime } from '../../../_shared/utils';
import { DialogService } from '../../../_services/dialog.service';
import { Cue5050BaseReport } from '../../../_models/fundraising/reports/Cue5050BaseReport';

@Component({
    selector: 'app-heatmap',
    templateUrl: './heatmap-report.component.html',
    styleUrls: ['./heatmap-report.component.css']
  })

export class HeatmapReportComponent implements OnInit, AfterViewInit, OnDestroy {

    @ViewChild('heatmapRef', { static: false }) heatmapRef: ElementRef;

    @ViewChild('tooltip', {static: false}) tooltipRef: ElementRef;

    @Input() 
    public event: Cue5050BaseReport;

    public ticketOrders: TicketOrder[] = [];

    public eventStartSinceEpoch: number;
    public eventEndSinceEpoch: number;

    public mouseX: number;
    public scale: number = 1;
    public currentZoom = 0;
    public zoomFactor = 2;
    public originX = 0;
    public scrollStack: number[] = [];
    public masterOrigin = 0;
    public topRight = 0;

    public displayStart: Date;
    public displayEnd: Date;
    public tooltipPosition: Date;
    public shouldDisplayTooltip = false;

    public canvas: any;
    public ctx: any;
    public resizeListener: any;

    constructor(
        private spinner: NgxSpinnerService,
        private cue5050Service: Cue5050Service,
        private dialogService: DialogService,
        private viewRef: ViewContainerRef
    ){}

    ngOnInit(){ 
        this.displayStart = this.event?.eventStartDate ? new Date(this.event.eventStartDate) : new Date(Date.now());
        this.displayEnd = this.event?.eventEndDate ? new Date(this.event.eventEndDate) : new Date(Date.now());

        this.eventStartSinceEpoch = this.displayStart.getTime();
        this.eventEndSinceEpoch = this.displayEnd.getTime();
    }

    ngAfterViewInit(){
        if (!this.heatmapRef)
            return;

        this.canvas = this.heatmapRef.nativeElement;
        this.ctx = this.canvas.getContext("2d");

        this.canvas.style.width = '100%';
        this.canvas.width = this.canvas.offsetWidth;
        this.topRight = this.canvas.width;

        this.spinner.show();
        this.cue5050Service.getTicketOrdersByEventId(this.event?.cue5050Id)
            .subscribe(data => {
                this.spinner.hide();
                this.ticketOrders = data;
                this.redraw();
            }, error => {
                this.spinner.hide();
                this.dialogService.alert('Uh oh...', "We couldn't find ticket orders for that event.", this.viewRef)
            });
        this.addResizeListener();
    }

    drawLine(time: number){
        const position = Math.floor(((time - this.eventStartSinceEpoch) * (this.canvas.width)) / (this.eventEndSinceEpoch - this.eventStartSinceEpoch));

        this.ctx.beginPath();
        this.ctx.moveTo(position, 0);
        this.ctx.lineTo(position, this.canvas.height);
        this.ctx.strokeStyle = '#ff000040';
        this.ctx.stroke();
        this.ctx.fill();
    }

    move(e: MouseEvent){
        const posX = e.clientX - this.canvas.offsetLeft;
        const posY = e.clientY - this.canvas.offsetTop;

        const posInMs = (((posX - this.originX) * (this.displayEnd.getTime() - this.displayStart.getTime())) / (this.canvas.width)) + this.displayStart.getTime();
        this.tooltipPosition = new Date(posInMs);

        const tooltip = this.tooltipRef.nativeElement;
        tooltip.style.top = (e.pageY + 20) + 'px';
        tooltip.style.left = (e.pageX + 20) + 'px';
    }

    redraw() {
        for (let order of this.ticketOrders){
            const utcDate = new Date(order.datePurchased);
            this.drawLine(new Date(utcDate.toLocaleString()).getTime());
        }

        // this.ctx.beginPath();
        // this.ctx.moveTo(this.originX, 0);
        // this.ctx.lineTo(this.originX, this.canvas.height);
        // this.ctx.strokeStyle = '#6a0dad';
        // this.ctx.stroke();
        // this.ctx.fill();

        // this.ctx.beginPath();
        // this.ctx.moveTo(this.topRight, 0);
        // this.ctx.lineTo(this.topRight, this.canvas.height);
        // this.ctx.strokeStyle = '#6a0dad';
        // this.ctx.stroke();
        // this.ctx.fill();
    }

    onZoom(e: any){
        e.preventDefault();
        if (!this.canvas || !this.ctx){
            return;
        }

        const oldOrigin = this.originX;
        const oldScale = this.scale;

        this.undoTransformations();

        if (e.deltaY > 0){
            if (this.currentZoom === 0){
                if (this.masterOrigin !== 0){
                    this.ctx.translate(-oldOrigin, 0);
                    this.masterOrigin = 0;
                    this.originX = 0;
                    this.clear();
                    this.calculateDisplayDates();
                    this.redraw();
                }
                return;
            }
            this.currentZoom -= 1;
            this.mouseX = this.scrollStack.pop();
        } else {
            if (this.currentZoom === 4){
                return;
            }
            this.currentZoom += 1;
            this.mouseX = e.clientX - this.canvas.offsetLeft;
            this.scrollStack.push(this.mouseX);
        }
        const newScale = Math.pow(this.zoomFactor, this.currentZoom);
        this.topRight = ((this.canvas.width - this.mouseX) / newScale) + this.mouseX;
        this.originX = ( this.mouseX / oldScale + oldOrigin - this.mouseX / newScale );
        this.ctx.scale(newScale, 1);
        this.masterOrigin -= this.originX;
        this.ctx.translate(-this.originX, 0);
        this.scale = newScale

        this.clear();
        this.calculateDisplayDates();
        this.redraw();
    }

    calculateDisplayDates(){
        if (!this.canvas){
            return;
        }
        this.displayStart = new Date(((Math.abs(this.masterOrigin) * (this.eventEndSinceEpoch - this.eventStartSinceEpoch))/this.canvas.width) + this.eventStartSinceEpoch);
        this.displayEnd = new Date(((this.topRight * (this.eventEndSinceEpoch - this.eventStartSinceEpoch))/this.canvas.width) + this.eventStartSinceEpoch);
    }

    onRangeChange(e: Event, end: boolean){
        if (!this.canvas || !this.ctx){
            return;
        }

        const oldOrigin = this.originX;
        const oldTopRight = this.topRight;
        this.undoTransformations();

        let right: number; 
        if (!end){
            const rangeStart = new Date((<HTMLInputElement>e.target).value).getTime();
            this.originX = Math.floor(((rangeStart - this.eventStartSinceEpoch) * (this.canvas.width)) / (this.eventEndSinceEpoch - this.eventStartSinceEpoch));
            this.masterOrigin -= this.originX;
            right = oldTopRight - this.originX;
            this.topRight = oldTopRight;
            this.scale = (this.canvas.width/right);
            this.ctx.scale(this.scale, 1);
            this.ctx.translate(-this.originX, 0);
        } else {
            const rangeEnd = new Date((<HTMLInputElement>e.target).value).getTime();
            right = Math.floor(((rangeEnd - this.eventStartSinceEpoch) * (this.canvas.width)) / (this.eventEndSinceEpoch - this.eventStartSinceEpoch));
            this.topRight = right;
            this.masterOrigin -= oldOrigin;
            this.scale = (this.canvas.width/right);
            this.originX = oldOrigin;
            this.ctx.scale(this.scale, 1);
            this.ctx.translate(-this.originX, 0);
        }

        this.clear();
        this.calculateDisplayDates();
        this.redraw();
    }

    onMouseEnter(){
        this.shouldDisplayTooltip = true;
    }

    onMouseExit(){
        this.shouldDisplayTooltip = false;
    }

    addResizeListener(){
        window.addEventListener('resize', this.resizeListener = e => {
            if (!this.canvas)
                return;

            this.canvas.width = this.canvas.offsetWidth;
            this.reset();
        })
    }

    reset(){
        this.currentZoom = 0;
        this.undoTransformations();
        this.clear();
        this.calculateDisplayDates();
        this.redraw();
    }

    undoTransformations(){
        this.masterOrigin += this.originX;
        this.ctx.translate(this.originX, 0);
        this.ctx.scale(1/this.scale, 1);
        this.originX = 0;
        this.scale = 1;
        this.topRight = this.canvas.width;
    }

    clear(){
        this.ctx.save();
        this.ctx.setTransform(1, 0, 0, 1, 0, 0);
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        this.ctx.restore();
    }

    ngOnDestroy(){
        window.removeEventListener('resize', this.resizeListener);
    }
}